/**
 * Attachments — the rows that say what is in object storage and who it belongs to.
 *
 * This package is imported by the native app and must stay free of Cloudflare types, so the bytes
 * are not this repository's business: the API Worker writes the object and then records it here,
 * and archiving returns the row so the same caller can delete what it wrote. The consequence worth
 * naming is that a crash between the two leaves an object with no row — storage that is paid for
 * and unreachable, which is the harmless direction. The other order would put a row on the public
 * page pointing at bytes that never arrived.
 *
 * `entityType` carries the role (`event.poster`, `announcement.image`), which is what lets the
 * public page fetch every image for a page's worth of updates in one query instead of one per
 * update — that page is the hottest in the system (D28).
 */

import { and, asc, eq, inArray, isNull } from "drizzle-orm";
import type { Permission } from "@powwow/validation";
import { announcements, attachments, events } from "@powwow/database/schema";
import { actorUserId } from "../actor.js";
import { Repository, type Database } from "../repository.js";
import { requirePermission } from "../tenant.js";

/**
 * Where an image can hang.
 *
 * A closed set rather than free text: `entityType` is a string column shared with everything else
 * the platform will eventually store, and a typo in it is a file that exists, costs money and is
 * displayed nowhere.
 */
export const ATTACHMENT_ROLES = [
  "event.poster",
  "announcement.image",
  /**
   * A community's own artwork — the logo, the favicon, the banner strip, a sponsor's mark.
   *
   * The odd one out in two ways, both of which follow from it belonging to the community rather
   * than to anything inside it. Its `entityId` is the community's own id, so the row is scoped to
   * itself; and it is the only role whose files are not referenced by a foreign key but by a string
   * inside `branding_json`, which is what makes the pruning in the branding route the thing that
   * keeps storage honest.
   */
  "community.brand",
] as const;
export type AttachmentRole = (typeof ATTACHMENT_ROLES)[number];

export interface AttachmentRow {
  id: string;
  entityType: string;
  entityId: string;
  r2Key: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  altText: string | null;
  width: number | null;
  height: number | null;
}

export interface AttachmentInput {
  r2Key: string;
  filename: string;
  mime: string;
  sizeBytes: number;
  altText?: string | null | undefined;
  width?: number | null | undefined;
  height?: number | null | undefined;
}

const COLUMNS = {
  id: attachments.id,
  entityType: attachments.entityType,
  entityId: attachments.entityId,
  r2Key: attachments.r2Key,
  filename: attachments.filename,
  mime: attachments.mime,
  sizeBytes: attachments.sizeBytes,
  altText: attachments.altText,
  width: attachments.width,
  height: attachments.height,
} as const;

/**
 * Look up one attachment for serving — a free function, not a method, because serving genuinely
 * has no tenant.
 *
 * The route that streams the bytes answers an `<img>` on a page that may have been cached at the
 * edge an hour ago: no session, no community header, no `Host` worth resolving. Every other read
 * in this package is tenant-scoped and must stay that way, so this is deliberately outside the
 * repository rather than a method that quietly skips `scoped()` — a reviewer should be able to see
 * that the unscoped query is the exception and read why here.
 *
 * What stands in for tenancy is the id: a ULID, only ever handed out inside a payload the reader
 * was already entitled to, naming an image intended for a public page.
 */
export async function findServableAttachment(
  db: Database,
  id: string,
): Promise<AttachmentRow | undefined> {
  const rows = await db
    .select(COLUMNS)
    .from(attachments)
    .where(and(eq(attachments.id, id), isNull(attachments.archivedAt)))
    .limit(1);
  return rows[0] as AttachmentRow | undefined;
}

export class AttachmentRepository extends Repository {
  /** One entity's images, in the order they were uploaded — which is the order they were meant. */
  async list(role: AttachmentRole, entityId: string): Promise<AttachmentRow[]> {
    return (await this.db
      .select(COLUMNS)
      .from(attachments)
      .where(
        this.scoped(
          attachments,
          eq(attachments.entityType, role),
          eq(attachments.entityId, entityId),
          isNull(attachments.archivedAt),
        ),
      )
      .orderBy(asc(attachments.id))) as AttachmentRow[];
  }

  /**
   * The same thing for a page's worth of entities at once, keyed by entity.
   *
   * The empty-list guard is not politeness: `IN ()` is a syntax error in SQLite, and an event with
   * no updates at all is the normal state of an event until the morning it runs.
   */
  async listByEntity(
    role: AttachmentRole,
    entityIds: readonly string[],
  ): Promise<Map<string, AttachmentRow[]>> {
    const grouped = new Map<string, AttachmentRow[]>();
    if (entityIds.length === 0) return grouped;

    // The caller sizes this list, so it is chunked rather than trusted — see `inChunks`.
    const rows = (await this.inChunks(entityIds, (chunk) =>
      this.db
        .select(COLUMNS)
        .from(attachments)
        .where(
          this.scoped(
            attachments,
            eq(attachments.entityType, role),
            inArray(attachments.entityId, chunk),
            isNull(attachments.archivedAt),
          ),
        )
        .orderBy(asc(attachments.id)),
    )) as AttachmentRow[];

    for (const row of rows) {
      const existing = grouped.get(row.entityId);
      if (existing) existing.push(row);
      else grouped.set(row.entityId, [row]);
    }
    return grouped;
  }

  async get(id: string): Promise<AttachmentRow> {
    const rows = await this.db
      .select(COLUMNS)
      .from(attachments)
      .where(this.scoped(attachments, eq(attachments.id, id), isNull(attachments.archivedAt)))
      .limit(1);
    return this.required(rows[0] as AttachmentRow | undefined, "attachment");
  }

  /**
   * The same check `record` makes, before the bytes are spent.
   *
   * Uploading is the one write in the system where the expensive part happens *before* the row: the
   * object has to exist for there to be a key to record. Without this, a volunteer who lacks the
   * permission — or a stale tab pointed at a deleted update — pushes a whole image over venue wifi
   * and is then told no. `record` checks again rather than trusting that this was called.
   */
  async assertCanAttach(role: AttachmentRole, entityId: string): Promise<void> {
    await this.check(role, entityId);
  }

  /**
   * Record a stored object. The permission check runs against the event the entity belongs to, and
   * resolving the entity is what proves it exists and is this community's — an unchecked
   * `entityId` would let a caller hang a file off another community's update.
   */
  async record(
    role: AttachmentRole,
    entityId: string,
    input: AttachmentInput,
  ): Promise<AttachmentRow> {
    await this.check(role, entityId);

    const values = this.insertValues({
      entityType: role,
      entityId,
      r2Key: input.r2Key,
      filename: input.filename,
      mime: input.mime,
      sizeBytes: input.sizeBytes,
      altText: input.altText ?? null,
      width: input.width ?? null,
      height: input.height ?? null,
      uploadedBy: actorUserId(this.context.actor),
    });
    await this.db.insert(attachments).values(values);

    await this.recordAudit({
      action: "attachment.create",
      entityType: "attachment",
      entityId: values.id,
      after: values,
    });
    return this.get(values.id);
  }

  /** Alt text is editable after the fact, because it is usually written after the fact. */
  async setAltText(id: string, altText: string | null): Promise<AttachmentRow> {
    const before = await this.get(id);
    await this.check(before.entityType as AttachmentRole, before.entityId);

    await this.db
      .update(attachments)
      .set(this.updateValues({ altText }))
      .where(this.scoped(attachments, eq(attachments.id, id)));

    const after = await this.get(id);
    await this.recordAudit({
      action: "attachment.update",
      entityType: "attachment",
      entityId: id,
      before,
      after,
    });
    return after;
  }

  /**
   * Archive, and hand the row back so the caller can delete the object it names.
   *
   * The row is soft-deleted like everything else here, which means the audit trail still says what
   * was on the page during the event even after the bytes are gone.
   */
  async archive(id: string): Promise<AttachmentRow> {
    const before = await this.get(id);
    await this.check(before.entityType as AttachmentRole, before.entityId);

    await this.db
      .update(attachments)
      .set(this.updateValues({ archivedAt: this.now() }))
      .where(this.scoped(attachments, eq(attachments.id, id)));

    await this.recordAudit({
      action: "attachment.archive",
      entityType: "attachment",
      entityId: id,
      before,
    });
    return before;
  }

  /**
   * The permission gate, in one place because all four writes need exactly the same one.
   *
   * Every method here resolves the same question — may this actor touch this file — and each used
   * to answer it in its own two lines. One of the four getting it wrong would be a silent hole, and
   * "silent" is the operative word: the failure is a file that uploads fine for someone who should
   * not have been able to upload it.
   */
  private async check(role: AttachmentRole, entityId: string): Promise<void> {
    const { permission, eventId } = await this.authorize(role, entityId);
    // Unscoped for community artwork, which belongs to no event — `community.manage` is held
    // community-wide or not at all, and a scope naming an event it has nothing to do with would
    // produce a refusal about the wrong thing.
    requirePermission(this.context, permission, eventId ? { event: eventId } : undefined);
  }

  /**
   * Which permission governs this file, and which event scopes it.
   *
   * Images on an update are governed by `announcement.manage` rather than by `event.manage`,
   * because the person posting "Grand Entry delayed to 1:30" with a photo of the schedule board is
   * exactly the person who was given the narrow key and not the wide one.
   */
  private async authorize(
    role: AttachmentRole,
    entityId: string,
  ): Promise<{ permission: Permission; eventId?: string }> {
    /*
     * Community artwork answers to the permission that owns the community's identity — the same one
     * that governs the colours and the welcome message it sits beside on the settings screen.
     *
     * The equality check is the tenancy boundary. Every other role resolves its entity through a
     * `scoped()` query, which is what proves the row is this community's; here the entity *is* the
     * community, so there is no row to look up and the proof has to be stated. Without it a caller
     * could hang a file off another community's id and have it recorded under their own.
     */
    if (role === "community.brand") {
      this.required(entityId === this.communityId ? entityId : undefined, "community");
      return { permission: "community.manage" };
    }

    if (role === "event.poster") {
      const rows = await this.db
        .select({ id: events.id })
        .from(events)
        .where(this.scoped(events, eq(events.id, entityId), isNull(events.archivedAt)))
        .limit(1);
      this.required(rows[0], "event");
      return { permission: "event.manage", eventId: entityId };
    }

    const rows = await this.db
      .select({ eventId: announcements.eventId })
      .from(announcements)
      .where(
        this.scoped(
          announcements,
          eq(announcements.id, entityId),
          isNull(announcements.archivedAt),
        ),
      )
      .limit(1);
    const row = this.required(rows[0] as { eventId: string } | undefined, "announcement");
    return { permission: "announcement.manage", eventId: row.eventId };
  }
}
